import type { I18nService } from '@core/i18n-legacy';
import { DatasetBase } from '@core/reports-types';
import { valueFormatter } from '@core/utils-legacy';

/**
 * Value (X) axis for rate metrics, anchored at the zero baseline.
 *
 * When every value is non-negative the axis keeps the familiar 0–100% range,
 * but grows past 100 when the data does (rates can exceed 100%). When any
 * value is negative the axis becomes fully dynamic and fits the data on both
 * sides of zero — the fixed 100 cap is dropped so the (typically smaller)
 * spread around zero stays readable. All-zero data is non-negative, so it
 * falls back to the plain 0–100 range instead of degenerating to a zero-width
 * axis.
 *
 * Labels are formatted through `valueFormatter` (driven by the metric
 * `unitType`) rather than a hardcoded `%`, so no assumption about the metric
 * being a percentage is baked in here.
 */
export const rateValueXAxis = (dataset: DatasetBase, i18n: I18nService) => ({
  type: <const>'value',
  min: ({ min }: { min: number; max: number }) => (min >= 0 ? 0 : min),
  max: ({ min, max }: { min: number; max: number }) =>
    min >= 0 ? Math.max(max, 100) : max <= 0 ? 0 : max,
  axisTick: { show: false },
  boundaryGap: <[number, number]>[0, 0],
  axisLabel: {
    show: true,
    formatter: (value: number) =>
      valueFormatter(
        value,
        {
          unitType: dataset.metric.unitType,
          unitPrecision: dataset?.meta?.unitPrecision ?? dataset.metric.unitPrecision,
        },
        i18n,
      ),
  },
});
